<template>
    <div class="wp-debug-tool">
        <a-descriptions title="" layout="vertical" bordered>
            <a-descriptions-item :label="$t('whatsapp.product')"> {{ getPlanName() }}</a-descriptions-item>
            <a-descriptions-item :label="$t('whatsapp.healthStatus')">
                <a-badge status="success" :text="$t('whatsapp.healthy')" v-if="isHealthy" />
                <a-badge status="error" :text="$t('whatsapp.notHealthy')" v-else />
            </a-descriptions-item>
            <a-descriptions-item :label="$t('whatsapp.whatsappName')">{{ getVerifiedName() }}</a-descriptions-item>
            <a-descriptions-item :label="$t('whatsapp.whatsappNumber')" :span="3"> {{ getPhoneNumber() }}</a-descriptions-item>

            <a-descriptions-item :label="$t('whatsapp.actions')" :span="4">
                <div class="debug-buttons-container">
                    <div class="buttons">
                        <a-button type="primary" ghost @click="sendRequest('getMe')">{{ $t('whatsapp.getMe') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('getConnectionState')">
                            {{ $t('whatsapp.getConnectionState') }}
                        </a-button>
                        <a-button type="primary" ghost @click="sendRequest('getHostNumber')">{{ $t('whatsapp.getHostNumber') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('getStatus')">{{ $t('whatsapp.getStatus') }}</a-button>
                        <a-button type="danger" ghost @click="sendRequest('kill')">{{ $t('whatsapp.kill') }}</a-button>
                        <a-button type="danger" ghost @click="sendRequest('reset')">{{ $t('whatsapp.reset') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('refresh')">{{ $t('whatsapp.refresh') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('syncContacts')">{{ $t('whatsapp.syncContacts') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('getWAVersion')">{{ $t('whatsapp.waVersion') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('healthCheck')">{{ $t('whatsapp.healthCheck') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('gc')">{{ $t('whatsapp.runGc') }}</a-button>
                        <a-button type="primary" ghost @click="sendRequest('emitUnreadMessages')">
                            {{ $t('whatsapp.getUnreadMessages') }}
                        </a-button>
                    </div>
                    <div class="response">
                        <span class="title">{{ $t('whatsapp.response') }}</span>
                        <span class="content" v-if="busy">{{ $t('whatsapp.waiting') }}</span>
                        <div class="content" v-else>
                            <prism-editor class="my-editor" v-model="response" :highlight="highlighter" readonly />
                        </div>
                    </div>
                </div>
            </a-descriptions-item>
        </a-descriptions>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import _ from "lodash";
import { generateRandomId } from "../../../../utils/session";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import { filter, take, first, timeout, map } from "rxjs/operators";
import log2 from "../../../../utils/log2";
import { PrismEditor } from "vue-prism-editor";
import "prismjs/themes/prism.css";
import "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";

const log = log2("WhatsappDebugTool");
export default {
    name: "WhatsappDebugTool",
    data() {
        return {
            busy: false,
            response: "",
        };
    },
    components: {
        PrismEditor,
    },
    computed: {
        ...mapGetters({
            isHealthy: "whatsapp/isHealthy",
        }),
        ...mapState({
            siteId: (state) => state.site.site._id,
            initialized: (state) => state.whatsapp.info.initialized,
            status: (state) => state.whatsapp.info.status,
            whatsappPlusEnabled: (state) => _.get(state.setting.settings, "whatsapp.plus.enabled", false),
            whatsappPlanExists: (state) =>
                _.get(state.setting.settings, "whatsapp.plus.enabled", false) ||
                _.get(state.setting.settings, "whatsapp.enabled", false),
            me: (state) => state.whatsapp.me,
        }),
    },

    methods: {
        highlighter(code) {
            // return highlight(code, languages.json); // languages.<insert language> to return html with markup
            return Prism.highlight(code, Prism.languages.json, "json");
        },
        getPlanName() {
            if (this.whatsappPlanExists) {
                if (this.whatsappPlusEnabled) {
                    return "Whatsapp Plus";
                } else {
                    return "Whatsapp";
                }
            }
        },
        getVerifiedName() {
            return _.get(this.me, "verifiedName") || _.get(this.me, "pushname");
        },
        getPhoneNumber() {
            return _.get(this.me, "me.user");
        },
        sendRequest(path) {
            const messageId = generateRandomId(5);
            this.busy = true;
            socketMessageStream
                .pipe(
                    map((x) => {
                        log("socketMessageStream", x);
                        return x;
                    }),
                    filter((x) => {
                        const { cmd, data } = x;
                        log("socket message", { cmd, data });
                        return cmd === "whatsapp_service_response" && data.messageId === messageId;
                    }),
                    timeout(5000),
                    first()
                )
                .toPromise()
                .then(({ data }) => {
                    log("response received", data);
                    const { response, path, success, error } = data;
                    this.response = success ? JSON.stringify(response, null, 2) : error;
                })
                .catch((err) => {
                    console.error(err);
                    this.response = "Request timed out";
                })
                .finally(() => {
                    this.busy = false;
                });

            sendSocketMessage("whatsapp send-request", {
                siteId: this.siteId,
                path,
                messageId,
                payload: {},
            });
        },
    },
    mounted() {
        log("mounted");
    },
};
</script>

<style lang="scss">
.wp-debug-tool {
    min-width: 800px !important;

    .debug-buttons-container {
        width: 100%;
        display: flex;
        gap: 10px;
        min-height: 200px;

        .buttons {
            width: 200px;
            display: flex;
            flex-direction: column;
            gap: 5px;
        }

        .response {
            width: 100%;
            height: auto;
            background-color: #f5f8fb;
            border: 1px solid #d1dfec;
            display: flex;
            position: relative;
            flex-direction: column;

            .title {
                padding: 5px;
                font-weight: 600;
                width: 100%;
                display: flex;
                justify-content: flex-end;
            }

            .content {
                padding: 5px;
                font-weight: 600;
                width: 100%;
                display: flex;
                justify-content: flex-start;
                color: #0e4d73;
            }
        }
    }
}

.debug-tool {
    .ant-modal {
        min-width: 900px !important;
    }
}
</style>
